/**
 * Utility for detecting contact information in messages before a booking is confirmed.
 * Used to enforce the platform communication policy.
 */

// Single-digit number words used to detect spelled-out phone numbers.
const SINGLE_DIGIT_WORDS = new Set([
  'zero', 'one', 'two', 'three', 'four',
  'five', 'six', 'seven', 'eight', 'nine',
]);

/**
 * Returns true if the text contains 7 or more consecutive single-digit number words,
 * which is the minimum length of a local phone number.
 * e.g. "seven one four five five five seven" → true
 */
const hasSpelledOutPhone = text => {
  const words = text.toLowerCase().split(/\s+/);
  let streak = 0;
  for (const word of words) {
    // Strip punctuation from each word before checking
    if (SINGLE_DIGIT_WORDS.has(word.replace(/[^a-z]/g, ''))) {
      streak++;
      if (streak >= 7) return true;
    } else {
      streak = 0;
    }
  }
  return false;
};

/**
 * Regex patterns that match contact information likely to facilitate off-platform communication.
 */
const CONTACT_INFO_PATTERNS = [
  // ── Direct contact info ────────────────────────────────────────────────────
  // Email addresses
  /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/,
  // Obfuscated email — "user at domain dot com" / "user (at) domain [dot] com"
  /\b\w[\w.\-]*\s*[\[(]?\s*at\s*[\])]?\s*[\w.\-\s]*?[\[(]?\s*dot\s*[\])]?\s*[a-z]{2,4}\b/i,
  // Phone numbers — 7+ digits with optional formatting characters
  /(\+?[\d][\d\s\-\.\(\)]{5,}[\d])/,
  // Spaced individual digits — "4 1 5 5 5 5 5 5 5 5"
  /\b(\d\s){6,}\d\b/,
  // URLs — block all external links EXCEPT AWS storage URLs used for chat file attachments.
  // Allowed: *.amazonaws.com (S3) and *.cloudfront.net (CDN)
  /((https?:\/\/|www\.)(?!([a-z0-9-]+\.)*amazonaws\.com)(?!([a-z0-9-]+\.)*cloudfront\.net)\S+)/i,
  // Social media @handles
  /@[a-zA-Z0-9_\.]{2,}/,

  // ── Blocked keywords ───────────────────────────────────────────────────────
  // Email & phone words
  /\b(email|phone)\b/i,
  // Email providers
  /\b(gmail|yahoo|hotmail|outlook|icloud|protonmail|aol|zoho|gmx|mail\.com)\b/i,
  // Social platforms
  /\b(instagram|facebook|fb\.com|twitter|tiktok|snapchat|whatsapp|telegram|linkedin|pinterest|youtube|signal|wechat|viber|kik|discord|skype|line)\b/i,
  // Off-platform payment services
  /\b(venmo|zelle|paypal|cashapp|cash\s*app|crypto|bitcoin|wise|transferwise)\b/i,

  // ── Intent phrases ─────────────────────────────────────────────────────────
  // Sharing contact / number
  /\bshare\s+(my\s+)?(number|numbers|contact|details|info|information)\b/i,
  /\b(my\s+)?(phone|cell|mobile|contact)\s+number\b/i,
  /\bgive\s+(you|me)\s+(my|your)\s+(number|contact|details)\b/i,
  /\bmy\s+email\s+(is|address)\b/i,
  // Asking to move off-platform
  /\b(reach|contact|find|connect\s+with)\s+me\s+(at|on|outside|off|directly|via|through)\b/i,
  /\b(dm|pm|message|text|call|ring)\s+me\b/i,
  /\boff\s*(platform|site|app|here)\b/i,
  /\b(outside|outside\s+of)\s+(the\s+)?(platform|site|app|fleurspace)\b/i,
  /\bcontinue\s+(this\s+)?(conversation|chat|discussion)\s+(off|outside|elsewhere)\b/i,
  // Asking for contact info
  /\b(what'?s?|give\s+me)\s+(your|ur)\s+(number|email|contact|instagram|whatsapp)\b/i,
  /\bhow\s+(can|do)\s+i\s+(reach|contact|call|text)\s+you\b/i,
];

/**
 * Transaction process states where a booking is considered confirmed.
 * Contact information is permitted once any of these states is reached.
 */
export const CONFIRMED_BOOKING_STATES = [
  'accepted',
  'delivered',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'purchased',
  'offer-accepted',
];

/**
 * Transaction processes for freelancer communications where contact info restrictions are removed.
 */
export const FREELANCER_PROCESSES = [
  'default-inquiry',
  'default-negotiation',
  'job-process',
];

/**
 * Returns true if the transaction process belongs to a freelancer modality.
 * @param {string} processName
 * @returns {boolean}
 */
export const isFreelancerProcess = processName => {
  return FREELANCER_PROCESSES.includes(processName);
};

/**
 * Returns true if the given text contains contact information
 * (phone, email, URL, social media handle, or payment service).
 *
 * @param {string} text
 * @returns {boolean}
 */
export const containsContactInfo = text => {
  if (!text) return false;
  if (CONTACT_INFO_PATTERNS.some(pattern => pattern.test(text))) return true;
  // Check for spelled-out phone numbers separately (requires word counting, not regex)
  return hasSpelledOutPhone(text);
};

/**
 * Returns true if the booking has been confirmed OR if the transaction
 * is a freelancer process, permitting contact information sharing.
 *
 * @param {string} processState - current transaction process state
 * @param {string} [processName] - current transaction process name
 * @returns {boolean}
 */
export const isBookingConfirmed = (processState, processName) => {
  if (processName && isFreelancerProcess(processName)) {
    return true;
  }
  return CONFIRMED_BOOKING_STATES.includes(processState);
};

